import React, { memo, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Checkbox, Flex, Input, InputNumber, Select, Switch, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { ActionInfo } from './action-info'

type FieldSchema = {
    type?: string
    title?: string
    description?: string
    enum?: unknown[]
}

type InputSchema = {
    properties?: Record<string, FieldSchema>
    required?: string[]
}

type FixedInputsProps = {
    schemaJson: string
    value: string
    onChange: (value: string) => void
    onValidityChange: (invalid: boolean) => void
}

export function FixedInputs({ schemaJson, value, onChange, onValidityChange }: FixedInputsProps) {
    const schema = useMemo(() => JSON.parse(schemaJson) as InputSchema, [schemaJson])
    const fixed = JSON.parse(value) as Record<string, unknown>
    const invalidFields = useRef<Record<string, boolean>>({})
    const handlers = useRef({
        fixed,
        onChange,
        onValidityChange,
    })

    useLayoutEffect(() => {
        handlers.current = {
            fixed,
            onChange,
            onValidityChange,
        }
    }, [fixed, onChange, onValidityChange])

    const validity = useCallback((key: string, invalid: boolean) => {
        invalidFields.current[key] = invalid
        handlers.current.onValidityChange(Object.values(invalidFields.current).some(Boolean))
    }, [])

    const set = useCallback((key: string, val: unknown, enabled = true) => {
        const next = { ...handlers.current.fixed }

        if (enabled) {
            next[key] = val
        } else {
            delete next[key]
        }

        handlers.current.fixed = next
        handlers.current.onChange(JSON.stringify(next))
    }, [])

    return (
        <Flex
            vertical
            gap="small"
        >
            {Object.entries(schema.properties ?? {}).map(([key, field]) => (
                <FixedInputField
                    key={key}
                    fieldKey={key}
                    field={field}
                    required={schema.required?.includes(key) ?? false}
                    valueJson={fixedValueJson(fixed, key)}
                    onChange={set}
                    onValidityChange={validity}
                />
            ))}
        </Flex>
    )
}

// Undefined when the field is not fixed.
function fixedValueJson(fixed: Record<string, unknown>, key: string) {
    if (!Object.prototype.hasOwnProperty.call(fixed, key)) {
        return undefined
    }

    return JSON.stringify(fixed[key])
}

// The value a field gets when the user fixes it.
function initialValue(field: FieldSchema): unknown {
    if (field.enum?.length) {
        return field.enum[0]
    }

    switch (field.type) {
        case 'boolean':
            return false
        case 'integer':
        case 'number':
            return 0
        case 'object':
            return {}
        case 'array':
            return []
        default:
            return ''
    }
}

type FixedInputFieldProps = {
    fieldKey: string
    field: FieldSchema
    required: boolean
    valueJson?: string
    onChange: (key: string, value: unknown, enabled?: boolean) => void
    onValidityChange: (key: string, invalid: boolean) => void
}

// A value edit must not rerender every schema field and its tooltip.
const FixedInputField = memo(function FixedInputField({
    fieldKey: key,
    field,
    required,
    valueJson,
    onChange,
    onValidityChange,
}: FixedInputFieldProps) {
    const { t } = useTranslation()
    const name = field.title ?? key
    const enabled = valueJson !== undefined
    const fixed: unknown = enabled ? JSON.parse(valueJson) : undefined

    const toggleFixed = (checked: boolean) => {
        if (checked) {
            onChange(key, initialValue(field))

            return
        }

        onValidityChange(key, false)
        onChange(key, undefined, false)
    }

    return (
        <Flex
            gap="small"
            align="start"
            className="flex-col sm:flex-row"
        >
            <Flex
                align="center"
                gap={4}
                className="w-full shrink-0 sm:w-56"
            >
                <Checkbox
                    aria-label={`${t('Fixed input')}: ${name}`}
                    checked={enabled}
                    onChange={(event) => toggleFixed(event.target.checked)}
                >
                    {name}
                    {required && (
                        <span
                            className="ml-1 text-red-500"
                            aria-label={t('Required')}
                        >
                            *
                        </span>
                    )}
                </Checkbox>
                <ActionInfo name={name}>
                    {[
                        field.description,
                        t('Fixed inputs stay the same; other inputs are filled from the conversation.'),
                    ]
                        .filter(Boolean)
                        .join('\n\n')}
                </ActionInfo>
            </Flex>
            <div className={enabled ? 'min-w-0 w-full sm:flex-1' : 'hidden'}>
                {enabled && (
                    <FixedValueInput
                        name={name}
                        field={field}
                        value={fixed}
                        onChange={(val) => onChange(key, val)}
                        onValidityChange={(invalid) => onValidityChange(key, invalid)}
                    />
                )}
            </div>
        </Flex>
    )
})

type FixedValueInputProps = {
    name: string
    field: FieldSchema
    value: unknown
    onChange: (value: unknown) => void
    onValidityChange: (invalid: boolean) => void
}

function FixedValueInput({ name, field, value, onChange, onValidityChange }: FixedValueInputProps) {
    const { t } = useTranslation()

    if (field.enum) {
        return (
            <Select
                aria-label={name}
                className="w-full"
                value={JSON.stringify(value)}
                options={field.enum.map((item) => ({
                    value: JSON.stringify(item),
                    label: String(item),
                }))}
                onChange={(val) => onChange(JSON.parse(val))}
            />
        )
    }

    if (field.type === 'boolean') {
        return (
            <Switch
                aria-label={name}
                checked={value === true}
                checkedChildren={t('Yes')}
                unCheckedChildren={t('No')}
                onChange={onChange}
            />
        )
    }

    if (field.type === 'integer' || field.type === 'number') {
        return (
            <InputNumber
                aria-label={name}
                className="w-full"
                value={value as number}
                precision={field.type === 'integer' ? 0 : undefined}
                onChange={onChange}
            />
        )
    }

    if (field.type === 'string') {
        return (
            <Input
                aria-label={name}
                value={String(value)}
                onChange={(event) => onChange(event.target.value)}
            />
        )
    }

    return (
        <JsonValue
            name={name}
            onValidityChange={onValidityChange}
            value={value}
            onChange={onChange}
        />
    )
}

type JsonValueProps = {
    name: string
    value: unknown
    onChange: (value: unknown) => void
    onValidityChange: (invalid: boolean) => void
}

function JsonValue({ name, value, onChange, onValidityChange }: JsonValueProps) {
    const { t } = useTranslation()
    const [text, setText] = useState(JSON.stringify(value, null, 2))
    const [invalid, setInvalid] = useState(false)

    const changeText = (next: string) => {
        setText(next)

        try {
            onChange(JSON.parse(next))
            setInvalid(false)
            onValidityChange(false)
        } catch {
            setInvalid(true)
            onValidityChange(true)
        }
    }

    return (
        <Flex
            vertical
            gap="small"
        >
            <Input.TextArea
                aria-label={name}
                value={text}
                autoSize={{ minRows: 2 }}
                onChange={(event) => changeText(event.target.value)}
            />
            {invalid && <Typography.Text type="danger">{t('Enter valid JSON')}</Typography.Text>}
        </Flex>
    )
}
